// Browser language detection and redirect utility
// SEO-Optimized URL Structure (Google 2025-2026 Guidelines)
// /en      → English Global (x-default)
// /de      → German (Germany)
// /nl      → Dutch (Netherlands)
// /en-ca   → English Canadian
// /da      → Danish (Denmark)

const LANG_PATHS = {
  "en": "/en",
  "en-US": "/en",
  "en-GB": "/en",
  "nl": "/nl",
  "nl-NL": "/nl",
  "nl-BE": "/nl",
  "de": "/de",
  "de-DE": "/de",
  "de-AT": "/de",
  "de-CH": "/de",
  "da": "/da",
  "da-DK": "/da",
  "en-CA": "/en-ca",
  "en-ca": "/en-ca",
};

// Paths that should trigger language detection
const LANDING_PATHS = ["/", "/en", "/nl", "/de", "/en-ca", "/da"];

// Storage key for user's language preference
const LANG_PREF_KEY = "cryptovip_lang_pref";
const LANG_DETECTED_KEY = "cryptovip_lang_detected";

/**
 * Get the best matching language path based on browser language
 */
export const getBrowserLanguagePath = () => {
  // Get browser languages (array of preferred languages)
  const browserLangs = navigator.languages || [navigator.language];
  
  for (const lang of browserLangs) {
    // Check exact match first
    if (LANG_PATHS[lang]) {
      return LANG_PATHS[lang];
    }
    
    // Check language code without region (e.g., "de" from "de-DE")
    const baseLang = lang.split("-")[0].toLowerCase();
    if (LANG_PATHS[baseLang]) {
      return LANG_PATHS[baseLang];
    }
  }
  
  // Default to English
  return "/en";
};

/**
 * Check if user has already been redirected or set a preference
 */
export const hasLanguagePreference = () => {
  return localStorage.getItem(LANG_PREF_KEY) !== null || 
         sessionStorage.getItem(LANG_DETECTED_KEY) !== null;
};

/**
 * Save user's language preference (when they manually select a language)
 */
export const saveLanguagePreference = (langPath) => {
  localStorage.setItem(LANG_PREF_KEY, langPath);
};

/**
 * Mark that language detection has been performed this session
 */
export const markLanguageDetected = () => {
  sessionStorage.setItem(LANG_DETECTED_KEY, "true");
};

/**
 * Get saved language preference
 */
export const getSavedLanguagePreference = () => {
  return localStorage.getItem(LANG_PREF_KEY);
};

/**
 * Clear language preference (useful for testing)
 */
export const clearLanguagePreference = () => {
  localStorage.removeItem(LANG_PREF_KEY);
  sessionStorage.removeItem(LANG_DETECTED_KEY);
};

/**
 * Check if current path is a landing page path
 */
export const isLandingPath = (path) => {
  return LANDING_PATHS.includes(path);
};

/**
 * Perform language detection and redirect if needed
 * Returns the path to redirect to, or null if no redirect needed
 */
export const detectAndRedirect = (currentPath) => {
  // Only redirect on landing pages
  if (!isLandingPath(currentPath)) {
    return null;
  }
  
  // Don't redirect if user has a saved preference or already detected this session
  if (hasLanguagePreference()) {
    return null;
  }
  
  // Get the best language path based on browser settings
  const suggestedPath = getBrowserLanguagePath();
  
  // Mark that we've done detection this session
  markLanguageDetected();
  
  // Only redirect if the suggested path is different from current
  if (suggestedPath !== currentPath && suggestedPath !== "/") {
    return suggestedPath;
  }
  
  return null;
};

/**
 * Get human-readable language name from path
 */
export const getLanguageNameFromPath = (path) => {
  const names = {
    "/": "English",
    "/en": "English",
    "/nl": "Nederlands",
    "/de": "Deutsch",
    "/en-ca": "English (Canada)",
    "/da": "Dansk",
  };
  return names[path] || "English";
};

export default {
  getBrowserLanguagePath,
  hasLanguagePreference,
  saveLanguagePreference,
  markLanguageDetected,
  getSavedLanguagePreference,
  clearLanguagePreference,
  isLandingPath,
  detectAndRedirect,
  getLanguageNameFromPath,
};
